import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../context/AuthContext';
import '../styles/Auth.css';

const MFASetup = () => {
  const [step, setStep] = useState(1); // 1: QR Code, 2: Verify, 3: Success
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const { setupMFA, confirmMFA } = useContext(AuthContext);
  const navigate = useNavigate();

  React.useEffect(() => {
    const initializeMFA = async () => {
      setLoading(true);
      const result = await setupMFA();
      if (result.success) {
        setQrCode(result.qrCode);
        setSecret(result.secret);
        setBackupCodes(result.backupCodes);
      } else {
        setError(result.error);
      }
      setLoading(false);
    };

    initializeMFA();
  }, [setupMFA]);

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');

    if (verificationCode.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    setLoading(true);

    const result = await confirmMFA(secret, verificationCode);

    if (result.success) {
      setStep(3);
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  const handleCopyBackupCodes = () => {
    const codesText = backupCodes.join('\n');
    navigator.clipboard.writeText(codesText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleComplete = () => {
    navigate('/dashboard');
  };

  if (loading && step === 1) {
    return <div style={{ padding: '20px' }}>Loading MFA setup...</div>;
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h1>Staff IT</h1>
        <h2>Setup Two-Factor Authentication</h2>

        {error && <div className="error-message">{error}</div>}

        {step === 1 && (
          <>
            <p>
              Scan this QR code with your authenticator app (Google Authenticator, Microsoft
              Authenticator, Authy, etc.)
            </p>

            {qrCode && (
              <div style={{ textAlign: 'center', margin: '20px 0' }}>
                <img src={qrCode} alt="QR Code" style={{ maxWidth: '200px' }} />
              </div>
            )}

            <div style={{ marginBottom: '20px', padding: '10px', backgroundColor: '#f0f0f0' }}>
              <p style={{ fontSize: '12px', margin: 0 }}>Manual Entry Key:</p>
              <p style={{ fontSize: '14px', fontWeight: 'bold', wordBreak: 'break-all' }}>
                {secret}
              </p>
            </div>

            <p style={{ fontSize: '12px', color: '#666' }}>
              Can't scan? Enter this code manually in your authenticator app.
            </p>

            <button
              onClick={() => setStep(2)}
              className="btn-primary"
              style={{ marginTop: '20px' }}
            >
              I've scanned the QR code
            </button>
          </>
        )}

        {step === 2 && (
          <>
            <p>Enter the 6-digit code from your authenticator app:</p>

            <form onSubmit={handleVerify}>
              <div className="form-group">
                <label htmlFor="code">Verification Code</label>
                <input
                  type="text"
                  id="code"
                  value={verificationCode}
                  onChange={(e) =>
                    setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                  }
                  placeholder="000000"
                  maxLength="6"
                  required
                  disabled={loading}
                  style={{ textAlign: 'center', letterSpacing: '5px', fontSize: '18px' }}
                />
              </div>

              <button type="submit" disabled={loading} className="btn-primary">
                {loading ? 'Verifying...' : 'Verify Code'}
              </button>
            </form>
          </>
        )}

        {step === 3 && (
          <>
            <div style={{ backgroundColor: '#d4edda', padding: '15px', borderRadius: '4px' }}>
              <p style={{ color: '#155724', margin: 0 }}>
                ✓ Two-Factor Authentication has been enabled!
              </p>
            </div>

            <div style={{ marginTop: '20px' }}>
              <h3>📋 Save Your Backup Codes</h3>
              <p>
                If you lose access to your authenticator, use these backup codes to access your
                account. Each code can only be used once.
              </p>

              <div
                style={{
                  backgroundColor: '#f0f0f0',
                  padding: '15px',
                  borderRadius: '4px',
                  fontFamily: 'monospace',
                  marginBottom: '15px',
                  maxHeight: '200px',
                  overflowY: 'auto',
                }}
              >
                {backupCodes.map((code, idx) => (
                  <div key={idx} style={{ padding: '5px 0' }}>
                    {code}
                  </div>
                ))}
              </div>

              <button
                onClick={handleCopyBackupCodes}
                style={{
                  padding: '8px 12px',
                  marginBottom: '15px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                {copied ? '✓ Copied to clipboard' : 'Copy Backup Codes'}
              </button>

              <p style={{ fontSize: '12px', color: '#666' }}>
                Store these codes in a safe place. You'll need them if you can't use your
                authenticator app.
              </p>
            </div>

            <button
              onClick={handleComplete}
              className="btn-primary"
              style={{ marginTop: '20px', width: '100%' }}
            >
              Continue to Dashboard
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default MFASetup;
